import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {RGBA} from '@Utils/color.util';
import {rgbaToRGBAString} from '@Utils/color.util';
import type * as Fabric from '@postermywall/fabricjs-2';
import {
  cloneFillObject,
  createSolidFill,
  createTransparentFill,
  DEFAULT_FILL_COLOR,
  FillTypes,
  getStoredFillColors,
  hasFill,
  hasGradientFill,
  hasLinearGradientFill,
  hasRadialGradientFill,
  isCompleteFillObject,
  isGradientFillObject,
} from '@PosterWhiteboard/classes/fill.types';
import type {FillBoxSize, FillObject} from '@PosterWhiteboard/classes/fill.types';
import {createFabricGradient} from '@PosterWhiteboard/classes/fill-gradient';
import {areDevAssertionsEnabled} from '@PosterWhiteboard/libraries/dev-assertions';

export {DEFAULT_FILL_COLOR, FillTypes};
export type {FillObject};

const TRANSPARENT_COLOR: RGBA = [0, 0, 0, 0];

export class Fill extends CommonMethods {
  public value: FillObject = createSolidFill(DEFAULT_FILL_COLOR);

  /** A fill that paints nothing. */
  public static createTransparent(): Fill {
    return Fill.fromObject(createTransparentFill());
  }

  /** The fill a shape starts with: solid black, as Fabric shapes have always painted until a fill is set. */
  public static createShapeDefault(): Fill {
    return new Fill();
  }

  public static fromObject(fillObject: FillObject): Fill {
    const fill = new Fill();
    fill.copyVals(fillObject);
    return fill;
  }

  public get fillType(): FillTypes {
    return this.value.fillType;
  }

  /**
   * A fill is replaced whole: each type stores different keys, so merging part of one fill into a fill of another type would leave keys
   * neither type has. An update that is not a whole fill is a writer bug, reported under dev assertions and ignored.
   */
  public override copyVals(fill?: unknown): void {
    if (fill === undefined) {
      return;
    }
    if (!isCompleteFillObject(fill)) {
      if (areDevAssertionsEnabled()) {
        console.error(`A fill update must be a whole fill of its type, got ${JSON.stringify(fill)}. Build it from the current fill with the fill helpers.`);
      }
      return;
    }
    this.value = cloneFillObject(fill);
  }

  /** The Fabric filler for a box of the given size; a fill that paints nothing is a fully transparent color. */
  public getFabricFill(box: FillBoxSize): Fabric.TFiller | string {
    if (isGradientFillObject(this.value)) {
      return createFabricGradient(this.value, {left: 0, top: 0, width: box.width, height: box.height});
    }
    return rgbaToRGBAString(this.value.fillType === FillTypes.SOLID ? this.value.color : TRANSPARENT_COLOR);
  }

  /** The Fabric filler for a shape's box, or null for a fill that paints nothing, so the shape draws no interior at all. */
  public getShapeFill(box: FillBoxSize): Fabric.TFiller | string | null {
    return this.hasFill() ? this.getFabricFill(box) : null;
  }

  public hasFill(): boolean {
    return hasFill(this.value);
  }

  public hasGradientFill(): boolean {
    return hasGradientFill(this.value);
  }

  /** The colors the fill stores, the one a fill that paints nothing keeps included. */
  public getFillColor(): RGBA[] {
    return getStoredFillColors(this.value);
  }

  public hasLinearGradientFill(): boolean {
    return hasLinearGradientFill(this.value);
  }

  public hasRadialGradientFill(): boolean {
    return hasRadialGradientFill(this.value);
  }

  /** Fresh arrays every time: the poster object this feeds is frozen once it reaches the store, and a later edit must never write into it. */
  public toObject(): FillObject {
    return cloneFillObject(this.value);
  }
}
